import React, { useMemo } from 'react';
import { XtramCheckbox, XtramField, XtramSelect } from '../../XtramModal/XtramModal';
import styles from '../ReportModalXtram.module.scss';

// Tax Authority payment methods valid for a VAT payment report (v3).
const DIRECT_DEBIT = 1;
const CREDIT_CARD = 2;

/**
 * How the report is filed: the method, whether to close the reporting month(s) / create a VAT closing
 * journal with it and, for automatic filing, how the payment is collected.
 * The report/PDF/PCN buttons live in the modal footer.
 */
const VatModalActions = ({
    t,
    direction,
    reportingWay,
    handleReportingWayChange,
    closeReportingMonth,
    setCloseReportingMonth,
    reportingMonthsOpen,
    isBiMonthly,
    createVatClosing,
    setCreateVatClosing,
    vatClosingAccount,
    setVatClosingAccount,
    accountCardOptions = [],
    numberOfPayments,
    setNumberOfPayments,
    paymentMethod,
    setPaymentMethod,
}) => {
    const reportingOptions = [
        { value: 'automatic', label: t('AutomaticTaxAuthorityReporting') },
        { value: 'manual', label: t('ManualReporting') },
        { value: 'unreported', label: t('Unreported') },
    ];

    const paymentMethodOptions = [
        { value: DIRECT_DEBIT, label: t('AccountChargeAuthorization') },
        { value: CREDIT_CARD, label: t('CreditCard') },
    ];

    const paymentCountOptions = [
        { value: 1, label: t('OnePayment') || '1' },
        { value: 2, label: t('TwoPayments') || '2' },
        { value: 3, label: t('ThreePayments') || '3' },
        { value: 4, label: t('FourPayments') || '4' },
    ];
    const effectivePayments = paymentMethod === CREDIT_CARD ? 1 : numberOfPayments;

    const cardOptions = useMemo(() => (
        (accountCardOptions || [])
            .filter((c) => c && c.cardId != null && c.cardId !== '' && c.value1 !== 'AddItem')
            .map((c) => ({ value: String(c.cardId), label: `${c.cardId} - ${c.name || ''}` }))
    ), [accountCardOptions]);

    const isReporting = reportingWay === 'manual' || reportingWay === 'automatic';

    return (
        <div className={styles.panel} dir={direction}>
            <div className={styles.panelHeader}>{t('VatReportingWay')}</div>
            <div className={styles.panelBody}>
                <div className={styles.options} role="radiogroup">
                    {reportingOptions.map((option) => {
                        const active = reportingWay === option.value;
                        return (
                            <button
                                key={option.value}
                                type="button"
                                role="radio"
                                aria-checked={active}
                                className={`${styles.option} ${active ? styles.optionActive : ''}`}
                                onClick={() => !active && handleReportingWayChange({ target: { value: option.value } })}
                            >
                                <span className={styles.optionDot} aria-hidden="true" />
                                {option.label}
                            </button>
                        );
                    })}
                </div>

                {isReporting && (
                    <>
                        <XtramCheckbox
                            checked={reportingMonthsOpen ? closeReportingMonth : false}
                            disabled={!reportingMonthsOpen}
                            onChange={setCloseReportingMonth}
                            label={reportingMonthsOpen
                                ? t(isBiMonthly ? 'AlsoCloseReportingMonths' : 'AlsoCloseReportingMonth')
                                : t('ReportingMonthAlreadyClosed')}
                        />
                        <XtramCheckbox
                            checked={createVatClosing}
                            onChange={setCreateVatClosing}
                            label={t('AlsoCreateVatClosing')}
                        />
                        {createVatClosing && (
                            <XtramField label={t('VatCurrentAccount') || 'מע"מ חו"ז'} size="full">
                                <XtramSelect
                                    direction={direction}
                                    options={cardOptions}
                                    value={cardOptions.find((o) => o.value === vatClosingAccount) || null}
                                    onChange={(option) => setVatClosingAccount(option ? option.value : '')}
                                    placeholder={t('SelectAccountCard') || 'Select account card'}
                                    error={!vatClosingAccount}
                                />
                            </XtramField>
                        )}
                    </>
                )}

                {reportingWay === 'automatic' && (
                    <>
                        <XtramField label={t('PaymentMethod')} size="full">
                            <XtramSelect
                                direction={direction}
                                options={paymentMethodOptions}
                                value={paymentMethodOptions.find((o) => o.value === paymentMethod) || null}
                                onChange={(option) => option && setPaymentMethod(option.value)}
                                disableClearable
                            />
                        </XtramField>
                        <XtramField label={t('NumberOfPayments') || 'Number of Payments'} size="full">
                            <XtramSelect
                                direction={direction}
                                options={paymentCountOptions}
                                value={paymentCountOptions.find((o) => o.value === effectivePayments) || null}
                                onChange={(option) => option && setNumberOfPayments(option.value)}
                                disabled={paymentMethod === CREDIT_CARD}
                                disableClearable
                            />
                        </XtramField>
                        <p className={styles.hint}>
                            {paymentMethod === CREDIT_CARD
                                ? t('CreditCardSinglePaymentHint')
                                : t('AccountChargeAuthorizationHint')}
                        </p>
                    </>
                )}
            </div>
        </div>
    );
};

export default VatModalActions;
